import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from 'three';
import { computeLogoPlacement, type LogoTextureSource } from './screenDisplay';
import type { LicensePlateConfig } from '../../types';

/**
 * 차량 번호판에 얹는 쇼룸 로고 판.
 *
 * 왜 필요한가:
 *   에셋들이 원저자가 살던 나라의 번호판을 그대로 들고 옵니다. Model S는
 *   머티리얼 이름이 아예 `Philippine_License_Plate_Recovered`이고, Boxster는
 *   독일식 딜러 번호판이 발광 텍스처까지 달려 있습니다. 쇼룸 차 일곱 대가
 *   서로 다른 나라 번호를 달고 있으면 한 매장의 차로 보이지 않습니다.
 *   그래서 번호판을 딜러 판처럼 로고 하나로 통일합니다.
 *
 * 왜 로고 이미지를 UV에 그대로 얹지 않고 캔버스에 다시 그리는가:
 *   로고는 1000x1000 정사각인데 번호판은 2:1(미국식)에서 4.6:1(유럽식)까지입니다.
 *   그대로 얹으면 좌우로 최대 4.6배 늘어나 로고를 알아볼 수 없습니다. 그래서 판
 *   비율에 맞는 캔버스를 만들고 그 안에 로고를 비율 유지로 앉힙니다.
 *   실내 화면(screenDisplay.ts)과 같은 방식이고, 자리 계산도 같은 함수를 씁니다.
 */

/**
 * 판 바닥색 기본값.
 *
 * 순백(#ffffff)이 아닌 이유: 실물 번호판은 완전한 흰색이 아니고, 순백으로 두면
 * 스튜디오 HDRI의 밝은 부분을 받는 각도에서 판 전체가 하얗게 타서 로고 윤곽이
 * 사라집니다. 살짝 내린 회백색이 어느 각도에서도 로고 대비를 유지합니다.
 *
 * 로고에 알파 채널이 있어서(1000x1000 VP8X + ALPH) 이 색이 로고 뒤로 그대로
 * 드러납니다. 알파가 없었다면 로고의 흰 사각형이 판 위에 겹쳐 보였을 겁니다.
 */
export const PLATE_BACKGROUND_COLOR = '#e9ebe8';

/**
 * 캔버스 세로 해상도. 가로는 판 비율을 곱해서 정합니다.
 *
 * 화면 텍스처(screenDisplay.ts)는 가로를 고정하는데 여기서는 세로를 고정합니다.
 * 번호판은 비율 폭이 넓어서(2:1~4.6:1) 가로를 고정하면 유럽식 긴 판의 세로가
 * 1/4로 줄어듭니다. 로고는 정사각이라 항상 세로에 먼저 걸리므로, 세로를 고정하는
 * 쪽이 어느 판에서든 같은 선명도를 냅니다.
 *
 * 256이면 충분한 근거: 가장 큰 판이 세로 0.147m입니다. 차 전체(4.9m)가 화면
 * 가로를 채울 때 판의 세로는 1920px 기준 40px 남짓이고, 번호판만 클로즈업해도
 * 200px을 넘기기 어렵습니다.
 */
const CANVAS_HEIGHT = 256;

/**
 * 로고가 판에서 차지할 최대 비율.
 *
 * 화면(0.62)보다 크게 잡은 이유: 화면은 여백이 있어야 "켜져 있는 디스플레이"로
 * 보이지만, 번호판은 인쇄물이라 여백이 넓으면 로고를 빼먹은 빈 판처럼 보입니다.
 * 로고가 정사각이라 이 값은 사실상 세로 여백만 정합니다. 긴 판에서 좌우가 크게
 * 남는 것은 딜러 판의 실제 모습이라 그대로 둡니다.
 *
 * 0.74가 상한인 이유 — Sorento가 정하는 값입니다:
 *   판의 UV가 캔버스를 다 쓰지 않는 에셋이 있습니다. 네 모델의 가시면 v 범위는
 *     Model S  0.000~1.000  (캔버스 전체)
 *     Model 3  4.000~5.000  (한 타일 전체)
 *     Boxster  0.031~0.969  (세로 93.8%)
 *     Sorento  0.113~0.945  (세로 83.2%)
 *   즉 Sorento 판에는 캔버스의 세로 5.5%~88.7% 구간만 나타납니다. 처음에 0.84로
 *   잡았더니 로고가 캔버스 8%~92%를 차지해서, Sorento에서만 "towncar" 글자
 *   아래쪽이 판 밖으로 잘려 나갔습니다.
 *
 *   중앙 정렬 기준으로 로고가 5.5%~88.7% 안에 들어오려면 세로 77.4% 이하여야
 *   합니다. 여유를 두어 0.74로 잡았습니다(로고가 13%~87%를 차지해 네 모델 모두
 *   안쪽에 들어옵니다).
 *
 *   모델별로 다르게 주지 않고 가장 좁은 판에 맞춘 이유는, 0.74와 0.84의 차이가
 *   눈에 띄지 않는 반면 모델마다 다른 값을 두면 로고 크기가 차마다 달라 보이기
 *   때문입니다. 판 비율이 크게 다른 에셋이 새로 들어오면 이 값을 다시 재세요.
 */
const LOGO_MAX_FILL = 0.74;

/**
 * 로고 텍스처를 번호판 비율에 맞춘 CanvasTexture로 굽습니다.
 *
 * @param logoTexture drei useTexture로 불러온 로고 (screenDisplay와 같은 파일)
 * @param plate 모델별 번호판 설정
 * @returns 캔버스를 못 쓰는 환경(jsdom)에서는 null. 그러면 판은 원본 그대로 남습니다.
 */
export function createPlateTexture(
  logoTexture: LogoTextureSource | null | undefined,
  plate: LicensePlateConfig
): CanvasTexture | null {
  /*
   * 런타임 값은 텍스처 로더가 만든 HTMLImageElement입니다. three가 image를 any로
   * 두고 있어서 여기서 크기와 그리기에 필요한 만큼만 좁혀 씁니다.
   */
  const image = logoTexture?.image as (CanvasImageSource & { width: number; height: number }) | null | undefined;
  if (!image) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.height = CANVAS_HEIGHT;
  canvas.width = Math.round(CANVAS_HEIGHT * plate.aspect);
  const context = canvas.getContext('2d');
  if (context === null) {
    // jsdom처럼 2D 컨텍스트가 없는 환경입니다.
    return null;
  }

  context.fillStyle = plate.backgroundColor ?? PLATE_BACKGROUND_COLOR;
  context.fillRect(0, 0, canvas.width, canvas.height);

  const placement = computeLogoPlacement({
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    logoWidth: image.width,
    logoHeight: image.height,
    maxFill: LOGO_MAX_FILL,
  });
  context.drawImage(image, placement.x, placement.y, placement.width, placement.height);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  /*
   * flipY는 true여야 합니다(CanvasTexture 기본값이지만 확인해서 고른 값이라 명시합니다).
   *
   * glTF 텍스처는 보통 flipY = false로 쓰는데, 네 에셋의 번호판 UV는 모두 v가
   * 아래에서 위로 흐릅니다. 판 정점의 v와 앱 좌표 y의 상관계수를 재서 확인했습니다
   * (네 판 모두 +1에 가까움). false로 두면 로고가 위아래로 뒤집힙니다.
   *
   * 좌우는 뒤집을 필요가 없습니다. u가 관찰자의 오른쪽으로 증가합니다 — 뒤 번호판은
   * u가 +z와 같이 가고(Boxster·Model 3·Model S 뒤·Sorento 뒤), 앞 번호판은 -z와
   * 같이 갑니다(Model S 앞·Sorento 앞). 차 뒤에 선 사람의 오른쪽이 +z, 차 앞에 선
   * 사람의 오른쪽이 -z이므로 둘 다 정방향입니다.
   */
  texture.flipY = true;
  /*
   * Model 3의 번호판 UV는 v가 4~5입니다(정확히 한 타일 몫이 4만큼 밀려 있습니다).
   * 반복 래핑이 아니면 v > 1이 가장자리 픽셀로 잘려서 판이 한 줄 색으로 채워집니다.
   *
   * 다른 세 모델의 UV는 0~1 안에 있어서 이 설정에 영향을 받지 않습니다.
   */
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  return texture;
}
